import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';

import { SYSTEM_SHORTCUTS, SystemShortcutAction } from 'systemShortcuts';

type ShortcutHandler = () => void;

type RegisteredShortcutHandler = {
  token: symbol;
  handler: ShortcutHandler;
};

type ShortcutRegistry = Record<SystemShortcutAction, RegisteredShortcutHandler[]>;

type SystemShortcutsContextValue = {
  registerAction: (action: SystemShortcutAction, handler: ShortcutHandler) => () => void;
};

const initialRegistry: ShortcutRegistry = {
  openFilters: []
};

const SystemShortcutsContext = createContext<SystemShortcutsContextValue | undefined>(undefined);

function matchesCombo(event: KeyboardEvent, combo: string) {
  const normalizedParts = combo
    .toLowerCase()
    .split('+')
    .map((part) => part.trim())
    .filter(Boolean);

  const keyPart = normalizedParts[normalizedParts.length - 1];
  const modifiers = new Set(normalizedParts.slice(0, -1));
  const expectsCtrl = modifiers.has('ctrl');
  const expectsMeta = modifiers.has('cmd') || modifiers.has('meta');
  const expectsAlt = modifiers.has('alt') || modifiers.has('option');
  const expectsShift = modifiers.has('shift');

  if (event.ctrlKey !== expectsCtrl) return false;
  if (event.metaKey !== expectsMeta) return false;
  if (event.altKey !== expectsAlt) return false;
  if (event.shiftKey !== expectsShift) return false;

  const normalizedKey = event.key.toLowerCase();

  if (keyPart === '.') {
    return normalizedKey === '.' || event.code === 'Period';
  }

  return normalizedKey === keyPart;
}

function isEditableTarget(target: EventTarget | null) {
  if (!(target instanceof HTMLElement)) return false;

  if (target.isContentEditable || target.closest('[contenteditable="true"]')) {
    return true;
  }

  const tagName = target.tagName.toLowerCase();

  if (tagName === 'input' || tagName === 'textarea' || tagName === 'select') {
    return true;
  }

  const role = target.getAttribute('role');
  return role === 'textbox' || role === 'searchbox' || role === 'combobox';
}

export function SystemShortcutsProvider({ children }: { children: React.ReactNode }) {
  const registryRef = useRef<ShortcutRegistry>(initialRegistry);

  const hasRegisteredAction = useCallback((action: SystemShortcutAction) => registryRef.current[action].length > 0, []);

  const dispatchAction = useCallback((action: SystemShortcutAction) => {
    const handlers = registryRef.current[action];
    const activeHandler = handlers[handlers.length - 1]?.handler;

    if (!activeHandler) return false;

    activeHandler();
    return true;
  }, []);

  const registerAction = useCallback((action: SystemShortcutAction, handler: ShortcutHandler) => {
    const token = Symbol(action);
    registryRef.current[action] = [...registryRef.current[action], { token, handler }];

    return () => {
      registryRef.current[action] = registryRef.current[action].filter((entry) => entry.token !== token);
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;

      for (const shortcut of SYSTEM_SHORTCUTS) {
        if (!matchesCombo(event, shortcut.combo)) continue;
        if (!shortcut.allowInEditable && isEditableTarget(event.target)) return;
        if (shortcut.requiresRegisteredAction && !hasRegisteredAction(shortcut.action)) return;

        const handled = dispatchAction(shortcut.action);

        if (handled) {
          event.preventDefault();
        }

        return;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [dispatchAction, hasRegisteredAction]);

  const value = useMemo<SystemShortcutsContextValue>(
    () => ({
      registerAction
    }),
    [registerAction]
  );

  return <SystemShortcutsContext.Provider value={value}>{children}</SystemShortcutsContext.Provider>;
}

export function useSystemShortcuts() {
  const context = useContext(SystemShortcutsContext);

  if (!context) {
    throw new Error('useSystemShortcuts must be used within SystemShortcutsProvider');
  }

  return context;
}
